import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, CircleHelp, GraduationCap, Play, RefreshCw, Sparkles } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../Common/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Common/dropdown-menu';
import { useTourStore } from '../../stores/tourStore';
import { getRequiredPathForStep } from '../../config/onboardingTours';
import { useIsSuperAdmin } from '../../stores/authStore';
import { useSystemStatus } from '../../hooks/useSystemStatus';

const MENU_ITEM_CLASS = 'gap-2.5 px-2.5 py-2 cursor-pointer';

export const HelpMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startTourStore = useTourStore((s) => s.startTour);
  const isSuperAdmin = useIsSuperAdmin();
  const { status: systemStatus } = useSystemStatus();

  const availableTours = [
    { id: 'welcome', name: t('onboarding.tours.welcome') },
    { id: 'dashboard', name: t('onboarding.tours.dashboard') },
    { id: 'userJourney', name: t('onboarding.tours.userJourney') },
    { id: 'genieArtifact', name: t('onboarding.tours.genieArtifact') },
    { id: 'genieImporter', name: t('onboarding.tours.genieImporter') },
    { id: 'knowledge', name: t('onboarding.tours.knowledge') },
  ];

  const startTour = (tourId: string) => {
    localStorage.removeItem(`tour-completed-${tourId}`);
    localStorage.removeItem(`tour-completed-${tourId}-tour`);
    startTourStore(tourId);
    const path = getRequiredPathForStep(tourId, 0);
    if (path) navigate(path);
  };

  const resetAllTours = () => {
    useTourStore.getState().skipTour();
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('tour-completed-')) localStorage.removeItem(key);
    });
    Object.keys(sessionStorage).forEach((key) => {
      if (key === 'tour-storage') sessionStorage.removeItem(key);
    });
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title={t('sidebar.helpMenu')}
          aria-label={t('sidebar.helpMenu')}
          data-tour="help-menu"
        >
          <CircleHelp className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64 p-1.5">
        <DropdownMenuLabel>{t('sidebar.helpMenu')}</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem className={MENU_ITEM_CLASS} onSelect={() => navigate('/documentation')}>
            <BookOpen className="h-4 w-4 text-muted-foreground" aria-hidden />
            {t('sidebar.documentation.root')}
          </DropdownMenuItem>
          <DropdownMenuItem className={MENU_ITEM_CLASS} onSelect={() => navigate('/changelog')}>
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
            {t('sidebar.whatsNew', "What's New")}
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="flex items-center gap-2">
          <GraduationCap className="h-3.5 w-3.5" aria-hidden />
          {t('sidebar.onboarding')}
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          {availableTours.map((tour) => (
            <DropdownMenuItem key={tour.id} className={MENU_ITEM_CLASS} onSelect={() => startTour(tour.id)}>
              <Play className="h-3.5 w-3.5 text-primary" aria-hidden />
              {tour.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem className={`${MENU_ITEM_CLASS} text-muted-foreground`} onSelect={resetAllTours}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {t('onboarding.restartAllTours')}
          </DropdownMenuItem>
        </DropdownMenuGroup>

        {isSuperAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className={MENU_ITEM_CLASS} onSelect={() => navigate('/status')}>
              <span className="flex h-4 w-4 items-center justify-center" aria-hidden>
                <span
                  className={cn(
                    'h-2 w-2 rounded-full',
                    systemStatus === 'operational' && 'bg-emerald-500',
                    systemStatus === 'degraded' && 'bg-amber-500 animate-pulse',
                    systemStatus === 'unknown' && 'bg-muted-foreground'
                  )}
                />
              </span>
              <span className="text-xs text-muted-foreground">
                {systemStatus === 'operational'
                  ? t('sidebar.statusOperational', 'Tous les systèmes opérationnels')
                  : systemStatus === 'degraded'
                    ? t('sidebar.statusDegraded', 'Performance dégradée')
                    : t('sidebar.statusChecking', 'Vérification du statut...')}
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
